import LiveMetricController from "../../../report/live_metric_controller";

// The super-admin canonical dialog. Add persists a chart and card immediately; Edit re-derives an
// existing card from a new config.
export default class extends LiveMetricController {
  static values = { nameGenerated: { type: Boolean, default: true } };

  connect() {
    // A name the user never touched keeps tracking the selection; once they type, it is theirs. An
    // EDITED card starts false when its stored name was hand-typed, so re-deriving cannot silently
    // rename someone's card out from under them.
    this.nameIsGenerated = this.nameGeneratedValue;
    this.syncAll();
    this.syncHiddenDimension();
    // The frame's server-rendered src carries only the tenant, so an EDITED card would preview a
    // default-shaped chart until the author touched a control. Reloading here builds the URL from every
    // control the form is holding, which is the card's full saved config.
    if (this.hasPreviewTarget) this.loadPreview();
  }

  // The same selection persisted as a NEW chart rather than staged onto the card being edited. The
  // form is a GET so it carries no token; this retargets it to the POST route and adds one for that
  // submission only, which keeps the token out of the staging GET's query string.
  pinAsNew(event) {
    const { pinUrl, csrf } = event.params;
    const form = this.element.closest("form") ?? this.element;
    // createElement, not an interpolated HTML string: the value goes in as text rather than markup.
    const token = document.createElement("input");
    token.type = "hidden";
    token.name = "authenticity_token";
    token.value = csrf;
    form.appendChild(token);
    form.method = "post";
    form.action = pinUrl;
    // requestSubmit, NOT submit: submit() skips the submit event, so neither the credit-card
    // sanitizer nor blankGeneratedName would run and a typed card number would post raw.
    form.requestSubmit();
  }

  // Blank so the server derives it: the client's precomputed names are keyed on dimension|series only,
  // so a ranked or companioned chart would post "Items Sold by Item" where the server writes
  // "Top 8 items by Items Sold".
  blankGeneratedName() {
    if (this.nameIsGenerated && this.hasNameTarget) this.nameTarget.value = "";
  }

  loadPreview() {
    if (!this.hasPreviewTarget) return;

    // Built ON TOP of the url's own query rather than replacing it: the preview tenant is already on
    // there, and a plain `?${params}` would drop it and silently preview against a different tenant
    // than the board behind the dialog.
    const url = new URL(this.previewUrlValue, window.location.origin);
    url.searchParams.set("metric_key", this.metricTarget.value);
    url.searchParams.set("dimension", this.selectedDimension());
    url.searchParams.set("series", this.selectedSeries());
    url.searchParams.set("companion", this.selectedCompanion());
    url.searchParams.set("limit", this.selectedLimit());
    url.searchParams.set(
      "sort",
      this.hasSortTarget ? this.sortTarget.value : "",
    );
    url.searchParams.set(
      "card_type",
      this.hasChartTypeTarget ? this.chartTypeTarget.value : "",
    );
    // Only meaningful alongside a limit, and MetricQuery refuses it without one.
    const rollup =
      this.hasRollupTarget &&
      this.rollupTarget.checked &&
      this.selectedLimit() !== "";
    url.searchParams.set("rollup", rollup ? "1" : "");
    url.searchParams.set("target_lines", this.selectedTargetLines());
    // setAttribute rather than .src: the attribute is what Turbo observes to refetch, and it is set even
    // where the custom element's property accessor isn't defined.
    this.previewTarget.setAttribute("src", `${url.pathname}${url.search}`);
  }
}
